import React, { useState, useEffect } from 'react';
import { useAuth } from "@/context/AuthContext";
import { P } from "@/config/permissionCodes";
import {
  Users,
  UserCheck,
  UserX,
  AlertCircle,
  Coffee,
  Clock,
  Sunrise,
  Moon,
  Timer,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  ChevronRight,
  Smartphone,
  Monitor,
  Fingerprint,
  MapPin,
  Calendar,
  BarChart3,
  PieChart,
} from 'lucide-react';
import { ATTENDANCE_STATUS, DEPARTMENTS, formatTime } from './attendanceData';

// Stats Card Component
const StatsCard = ({ icon: Icon, label, value, subValue, trend, trendValue, color, onClick }) => {
  const colorClasses = {
    emerald: 'bg-emerald-100 text-emerald-600',
    red: 'bg-red-100 text-red-600',
    amber: 'bg-amber-100 text-amber-600',
    blue: 'bg-blue-100 text-blue-600',
    violet: 'bg-violet-100 text-violet-600',
    orange: 'bg-orange-100 text-orange-600',
    indigo: 'bg-indigo-100 text-indigo-600',
  };

  return (
    <div
      className={`bg-white rounded-xl border border-gray-200 p-4 ${onClick ? 'cursor-pointer hover:shadow-md hover:border-blue-300 transition' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between mb-3">
        <div className={`w-10 h-10 ${colorClasses[color]} rounded-lg flex items-center justify-center`}>
          <Icon size={20} />
        </div>
        {trend && (
          <div className={`flex items-center gap-1 text-xs font-medium ${trend === 'up' ? 'text-emerald-600' : 'text-red-600'}`}>
            {trend === 'up' ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
            {trendValue}
          </div>
        )}
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-500">{label}</p>
      {subValue && <p className="text-xs text-gray-400 mt-1">{subValue}</p>}
    </div>
  );
};

// Weekly Trend Chart Component
const WeeklyTrendChart = ({ data, onBarClick }) => {
  const maxValue = Math.max(...data.map(d => d.present + d.late));

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900">Weekly Attendance Trend</h3>
        <button className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
          <BarChart3 size={14} />
          View Details
        </button>
      </div>

      <div className="flex items-end justify-between gap-3 h-44">
        {data.map((day, idx) => {
          const presentHeight = (day.present / maxValue) * 140;
          const lateHeight = (day.late / maxValue) * 140;
          const total = day.present + day.late;

          return (
            <div
              key={idx}
              className="flex-1 flex flex-col items-center gap-1 group cursor-pointer"
              onClick={() => onBarClick?.(day)}
            >
              {/* Tooltip */}
              <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-gray-800 text-white text-xs rounded-lg px-2.5 py-1.5 mb-1 whitespace-nowrap shadow-lg">
                <div className="flex flex-col gap-0.5">
                  <span className="flex items-center gap-1.5">
                    <span className="w-2 h-2 bg-emerald-400 rounded-full"></span>
                    Present: {day.present}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-2 h-2 bg-amber-400 rounded-full"></span>
                    Late: {day.late}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-2 h-2 bg-red-400 rounded-full"></span>
                    Absent: {day.absent}
                  </span>
                </div>
              </div>

              <div className="w-full flex flex-col items-center" style={{ height: 140 }}>
                <div className="w-full flex flex-col justify-end h-full gap-0.5">
                  <div
                    className="w-full bg-emerald-500 rounded-t group-hover:bg-emerald-600 transition-colors"
                    style={{ height: presentHeight }}
                  />
                  {day.late > 0 && (
                    <div
                      className="w-full bg-amber-400 group-hover:bg-amber-500 transition-colors"
                      style={{ height: lateHeight }}
                    />
                  )}
                  {day.absent > 0 && (
                    <div
                      className="w-full bg-red-400 rounded-b group-hover:bg-red-500 transition-colors"
                      style={{ height: (day.absent / maxValue) * 140 }}
                    />
                  )}
                </div>
              </div>

              <span className="text-xs font-medium text-gray-600 group-hover:text-blue-600 transition-colors">
                {day.day}
              </span>
              <span className="text-xs text-gray-400">{total}</span>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t border-gray-100">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-emerald-500 rounded" />
          <span className="text-xs text-gray-600">Present</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-amber-400 rounded" />
          <span className="text-xs text-gray-600">Late</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 bg-red-400 rounded" />
          <span className="text-xs text-gray-600">Absent</span>
        </div>
      </div>
    </div>
  );
};

// Live Status Feed Component
const LiveStatusFeed = ({ attendanceLog, onViewAll, onEmployeeClick }) => {
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = () => {
    setRefreshing(true);
    setTimeout(() => setRefreshing(false), 1000);
  };

  const getMethodIcon = (method) => {
    const icons = {
      biometric: Fingerprint,
      mobile: Smartphone,
      web: Monitor,
    };
    const Icon = icons[method] || Monitor;
    return <Icon size={12} className="text-gray-400" />;
  };

  const getStatusDot = (status) => {
    const colors = {
      present: 'bg-emerald-500',
      late: 'bg-amber-500',
      absent: 'bg-red-400',
      leave: 'bg-blue-400',
    };
    return colors[status] || 'bg-gray-300';
  };

  // Sort by most recent check-in
  const sortedLog = [...attendanceLog]
    .filter(a => a.checkIn)
    .sort((a, b) => {
      const timeA = a.checkIn?.split(':').map(Number) || [0, 0];
      const timeB = b.checkIn?.split(':').map(Number) || [0, 0];
      return (timeB[0] * 60 + timeB[1]) - (timeA[0] * 60 + timeA[1]);
    })
    .slice(0, 8);

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden h-full">
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-gray-900">Live Status</h3>
          <span className="flex items-center gap-1 text-xs text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
            <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
            Live
          </span>
        </div>
        <button
          onClick={handleRefresh}
          className={`p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition ${refreshing ? 'animate-spin' : ''}`}
          title="Refresh"
        >
          <RefreshCw size={16} />
        </button>
      </div>

      <div className="divide-y divide-gray-100 max-h-72 overflow-y-auto">
        {sortedLog.map((emp) => (
          <div
            key={emp.id}
            className="px-4 py-2.5 flex items-center justify-between hover:bg-gray-50 cursor-pointer transition"
            onClick={() => onEmployeeClick?.(emp)}
          >
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold text-xs">
                  {emp.name.split(' ').map(n => n[0]).join('')}
                </div>
                <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 ${getStatusDot(emp.status)} rounded-full border-2 border-white`} />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">{emp.name}</p>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span>In: {formatTime(emp.checkIn)}</span>
                  {emp.checkOut && <span>• Out: {formatTime(emp.checkOut)}</span>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {getMethodIcon(emp.method)}
              <ChevronRight size={14} className="text-gray-300" />
            </div>
          </div>
        ))}
      </div>

      <div className="px-4 py-2 bg-gray-50 border-t border-gray-200">
        <button
          onClick={onViewAll}
          className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
        >
          View All Activity
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
};

// Department Summary Component
const DepartmentSummary = ({ attendanceLog, onDepartmentClick }) => {
  const deptStats = DEPARTMENTS.map(dept => {
    const deptEmployees = attendanceLog.filter(a => a.department === dept.name);
    const present = deptEmployees.filter(a => a.status === 'present' || a.status === 'late').length;
    const absent = deptEmployees.filter(a => a.status === 'absent').length;
    const onLeave = deptEmployees.filter(a => a.status === 'leave').length;
    const total = deptEmployees.length;

    return {
      ...dept,
      present,
      absent,
      onLeave,
      total,
      percentage: total > 0 ? Math.round((present / total) * 100) : 0,
    };
  }).filter(d => d.total > 0);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-gray-900">Department Overview</h3>
        <PieChart size={18} className="text-gray-400" />
      </div>
      <p className="text-xs text-gray-500 mb-4">Today's attendance rate by department</p>

      <div className="space-y-4">
        {deptStats.map((dept) => (
          <div
            key={dept.id}
            className="cursor-pointer hover:bg-gray-50 p-2 -mx-2 rounded-lg transition"
            onClick={() => onDepartmentClick?.(dept)}
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-gray-900">{dept.name}</span>
              <div className="flex items-center gap-2">
                <span className={`text-sm font-semibold ${
                  dept.percentage >= 90 ? 'text-emerald-600' :
                  dept.percentage >= 70 ? 'text-amber-600' : 'text-red-600'
                }`}>{dept.percentage}%</span>
              </div>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-1.5">
              <div
                className={`h-full rounded-full transition-all ${
                  dept.percentage >= 90 ? 'bg-emerald-500' :
                  dept.percentage >= 70 ? 'bg-amber-500' : 'bg-red-500'
                }`}
                style={{ width: `${dept.percentage}%` }}
              />
            </div>
            <div className="flex items-center gap-3 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                {dept.present} present
              </span>
              <span className="flex items-center gap-1">
                <span className="w-2 h-2 bg-red-400 rounded-full"></span>
                {dept.absent} absent
              </span>
              {dept.onLeave > 0 && (
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 bg-blue-400 rounded-full"></span>
                  {dept.onLeave} leave
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Quick Actions Component
const QuickActions = ({ onAction }) => {
  const actions = [
    { id: 'mark', label: 'Mark Attendance', icon: UserCheck, color: 'emerald', description: 'Manually mark attendance' },
    { id: 'regularize', label: 'Regularization', icon: Clock, color: 'blue', description: 'Request corrections' },
    { id: 'report', label: 'Generate Report', icon: BarChart3, color: 'violet', description: 'Create attendance report' },
  ];

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <h3 className="font-semibold text-gray-900 mb-4">Quick Actions</h3>
      <div className="space-y-2">
        {actions.map((action) => {
          const Icon = action.icon;
          const colorClasses = {
            emerald: 'bg-emerald-100 text-emerald-600 group-hover:bg-emerald-200',
            blue: 'bg-blue-100 text-blue-600 group-hover:bg-blue-200',
            violet: 'bg-violet-100 text-violet-600 group-hover:bg-violet-200',
          };

          return (
            <button
              key={action.id}
              onClick={() => onAction?.(action.id)}
              className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition group text-left"
            >
              <div className={`w-9 h-9 ${colorClasses[action.color]} rounded-lg flex items-center justify-center transition`}>
                <Icon size={18} />
              </div>
              <div>
                <p className="font-medium text-gray-900 group-hover:text-blue-600 transition">{action.label}</p>
                <p className="text-xs text-gray-500">{action.description}</p>
              </div>
              <ChevronRight size={16} className="ml-auto text-gray-300 group-hover:text-blue-500 transition" />
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Main Dashboard Component
export default function AttendanceDashboard({
  stats,
  weeklyData,
  attendanceLog,
  onViewAllActivity,
  onEmployeeClick,
  onDepartmentClick,
  onQuickAction,
  onBarClick,
}) {
  const { hasPermission } = useAuth();

const canViewAttendance = hasPermission(P.HRM.ATTENDANCE.SCREEN);
const canMarkAttendance = hasPermission(P.HRM.ATTENDANCE.DAILY.MARK);
const canEditAttendance = hasPermission(P.HRM.ATTENDANCE.DAILY.EDIT);
 if (!canViewAttendance) {
  return (
    <div className="flex items-center justify-center h-full py-24">
      <div className="text-center">

        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          Access Denied
        </h3>

        <p className="text-sm text-slate-500">
          You don't have permission to view this page.
        </p>

      </div>
    </div>
  );
}
return (
    <div className="space-y-6">
      {/* Stats Cards Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
        <div className="col-span-2">
          <StatsCard
            icon={UserCheck}
            label="Present Today"
            value={stats.present}
            subValue={`${stats.attendanceRate}% attendance rate`}
            color="emerald"
            trend="up"
            trendValue="+2%"
          />
        </div>

        <StatsCard
          icon={UserX}
          label="Absent"
          value={stats.absent}
          color="red"
          subValue={`${Math.round((stats.absent / stats.total) * 100)}% of total`}
        />

        <StatsCard
          icon={AlertCircle}
          label="Late Arrivals"
          value={stats.late}
          color="amber"
        />

        <StatsCard
          icon={Coffee}
          label="On Leave"
          value={stats.onLeave}
          color="blue"
        />

        <StatsCard
          icon={Sunrise}
          label="Avg Check-In"
          value={stats.avgCheckIn}
          color="orange"
        />

        <StatsCard
          icon={Moon}
          label="Avg Check-Out"
          value={stats.avgCheckOut}
          color="indigo"
        />

        <StatsCard
          icon={Timer}
          label="Avg Hours"
          value={stats.avgHours}
          color="violet"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <WeeklyTrendChart data={weeklyData} onBarClick={onBarClick} />
        </div>
        <LiveStatusFeed
          attendanceLog={attendanceLog}
          onViewAll={onViewAllActivity}
          onEmployeeClick={onEmployeeClick}
        />
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <DepartmentSummary
            attendanceLog={attendanceLog}
            onDepartmentClick={onDepartmentClick}
          />
        </div>
        {canMarkAttendance && (
        <QuickActions onAction={onQuickAction} />
      )}
      </div>
    </div>
  );
}
